import CircularProgress from "@mui/material/CircularProgress";
import React from "react";
interface ButtonProps {
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
  styles?: React.CSSProperties;
  type?: "button" | "submit" | "reset";
  variant?: "primary" | "secondary" | "link" | "danger";
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

const Button: React.FC<ButtonProps> = ({
  onClick,
  children,
  className,
  styles = {},
  type = "button",
  loading = false,
  disabled = false,
  variant = "primary",
}) => {
  /* The `baseStyle` constant in the Button component is defining the base CSS properties for the
  button element. It sets various styles such as width, font weight, display, border radius,
  padding, font size, alignment, transition effect, and cursor based on the provided values or
  defaults. The `...styles` spread operator allows additional custom styles to be passed to the
  button component and merged with the base styles. */
  const baseStyle: React.CSSProperties = {
    width: "100%",
    fontWeight: 500,
    display: "flex",
    borderRadius: "8px",
    padding: "8px 16px",
    fontSize: "0.875rem",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s ease",
    cursor: disabled ? "not-allowed" : "pointer",
    ...styles,
  };

  /* The `variantClass` constant is an object that maps different button variants to their
  corresponding CSS classes. Each key in the object represents a button variant (e.g., "primary",
  "secondary", "link", "danger"), and the value associated with each key is a string containing the
  CSS classes specific to that variant. */
  const variantClass: Record<string, string> = {
    primary:
      "bg-[#4a88ee] text-[#e7e7ec] hover:bg-[#eff6ff] hover:text-[#4a88ee] disabled:bg-[#f5f5f5] disabled:text-[#9292a6]",
    secondary:
      "bg-white border border-[#4a88ee] text-[#4a88ee] hover:bg-[#eff6ff] disabled:border-[#d9d9d9] disabled:text-[#d9d9d9] disabled:bg-white",
    link: "bg-white text-[#4a88ee] hover:text-[#b6b6c3] disabled:text-[#d9d9d9]",
    danger:
      "bg-transparent border border-[#ef4444] text-[#ef4444] hover:bg-[#ffe2e2] disabled:text-[#d9d9d9] disabled:border-[#d9d9d9]",
  };

  return (
    <button
      type={type}
      className={`${variantClass[variant]} ${className || ""}`}
      style={baseStyle}
      disabled={disabled || loading}
      onClick={onClick}
    >
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "0.5rem" }}>
          Loading
          <CircularProgress size={16} color="inherit" />
        </div>
      ) : (
        <div className="flex items-center gap-2">{children}</div>
      )}
    </button>
  );
};

export default Button;
